import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { generatePath, useLocation, useParams } from 'react-router';
import { toast } from 'sonner';
import { useErrorMessages, useFullscreenModal, useUserSession } from '../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../models/Logging';
import { getDisplayName, getPrincipalInitials } from '../../../models/NameUtils';
import {
	createContentStateWithHtmlStringValue,
	getContactCompanyLine,
	getMultilineFormattedAddress,
	hasRequiredHwcRecipientAddressFields,
	isAddressComplete,
} from '../../../models/UiUtils';
import {
	invalidateHandwrittenCardAllCategoryTemplates,
	invalidateMyTemplates,
	useHWCOrderMutation,
	useTemplate,
	useTemplateDeleteMutation,
	useUserHwcPreferences,
	useUserQuery,
} from '../../../queries';
import { Button } from '../../components-v2/Button';
import { ButtonGroup } from '../../components-v2/ButtonGroup';
import { Dropdown, DropdownContent, DropdownItem, DropdownTrigger } from '../../components-v2/Dropdown';
import { TextArea } from '../../components-v2/TextArea';
import { Avatar2 } from '../../components/Avatar2';
import { DayPicker } from '../../components/DayPicker';
import { DeleteConfirmation } from '../../components/DeleteConfirmation';
import { DeprecatedCloseButton } from '../../components/DeprecatedCloseButton';
import { DeprecatedPopover, PopoverType } from '../../components/DeprecatedPopover';
import { FabContext } from '../../components/FabContext';
import { GlobalSearch } from '../../components/GlobalSearch';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { Modal } from '../../components/Modal';
import { MultiContainerHeader } from '../../components/MultiContainerHeader';
import { SendFromDropdown } from '../../components/SendFromDropdown';
import { PurchaseCreditsModal } from '../../components/settings/PurchaseCreditsModal';
import { ChevronDown } from '../../components/svgs/icons/ChevronDown';
import { PostcardPostage } from '../../components/svgs/icons/PostcardPostage';
import { PostcardSpec } from '../../components/svgs/icons/PostcardSpec';
import { PostcardType } from '../../components/svgs/icons/PostcardType';
import { TextInput } from '../../components/TextInput';
import { baseStyleSheet, bs } from '../../styles/styles';
import { allowedSendersForSignatureEditing, cardSignaturePlaceholderText } from '../BulkHwcPages/BulkHwcSignatureField';
import { useHwcSaveTemplate } from '../BulkHwcPages/hooks/useHwcSaveTemplate';
import { SaveTemplateModal } from '../BulkHwcPages/presentation';
import { AddressFormField } from './AddressFormField';
import { useCurrentUserGiftingBalance } from './hooks/useCurrentUserGiftingBalance';
import { HANDWRITTEN_CARD_TEXT_MAX_ALLOWED_CHARACTERS, usePostcardTextEditor } from './hooks/usePostcardTextEditor';
import { useRecipient } from './hooks/useRecipient';
import { HwcFrontBackPreview } from './HwcFrontBackPreview';
import { HwcOrderSuccessPage } from './HwcOrderSuccessPage';
import { HwcTogglePreviewValues } from './HwcPreviewToggle';
import { PostcardTextEditor } from './PostcardTextEditor';
import { addresseeNameFromHwcRecipient, RecipientAddressModal } from './RecipientAddressModal';
import { ReturnContactModal } from './ReturnAddressModal';
import { styleSheet } from './styles';

export enum PostcardComposerStep {
	Preview = 'preview',
	Personalize = 'personalize',
	Order = 'order',
	Recipient = 'recipient',
	Success = 'success',
}

export const addressTextFromHwcRecipient = (recipient: Api.IHandwrittenCardRecipient) => {
	const displayName =
		recipient.address?.name ?? getDisplayName({ firstName: recipient.firstName, lastName: recipient.lastName });
	return `${displayName ? `${displayName}\n` : ''}${getMultilineFormattedAddress(recipient.address)}`;
};

export const addressTextFromHwcReturnContact = (returnContact: Api.IHandwrittenCardReturnContact) => {
	const displayName = getDisplayName({ firstName: returnContact.firstName, lastName: returnContact.lastName });

	return `${displayName ? `${displayName}\n` : ''}${getMultilineFormattedAddress(returnContact.address)}`;
};

export function PostcardComposer() {
	const userSession = useUserSession();
	const fullscreenModal = useFullscreenModal();
	const errorMessages = useErrorMessages();
	const { logApiError } = useEventLogging('PostcardComposer');
	const { step, templateId } = useParams<{
		step: PostcardComposerStep;
		templateId: string;
	}>();
	const location = useLocation<{ contactId?: string }>();

	const [selectedContact, setSelectedContact] = React.useState<Api.IContact>(null);
	const contactId = selectedContact?.id ?? location.state?.contactId;
	const templateQuery = useTemplate<Api.IHandwrittenCardTemplate>({ templateId });
	const [postcardContent, setPostcardContent] = React.useState('');
	const [sendFrom, setSendFrom] = React.useState<Api.ISendOnBehalfPermission>(
		userSession.defaultSendOnBehalfPermissions.find(x => x.sender.mode === Api.SendEmailFrom.CurrentUser)
	);
	const [sendFromUser, setSendFromUser] = React.useState<Api.IUser>(null);
	const disableSignatureEditing =
		sendFrom?.sender?.mode && !allowedSendersForSignatureEditing.find(x => x === sendFrom.sender.mode);
	const {
		createTemplateMutation,
		handleSaveAsButtonClicked,
		handleSaveAsModalClose,
		handleSaveAsModalSave,
		handleSaveButtonClicked,
		isSaveAllowed,
		isShowingSaveAsModal,
		updatingTemplateStatus,
	} = useHwcSaveTemplate({
		onSavedNewTemplate: template => {
			invalidateMyTemplates({ templateType: Api.TemplateType.HandwrittenCard });
			invalidateHandwrittenCardAllCategoryTemplates();

			fullscreenModal.history.replace({
				pathname: generatePath('/postcard/:templateId/:step', {
					step,

					templateId: template.id,
				}),
				state: location.state,
			});
		},
		postcardContent,

		template: templateQuery.data,
	});
	React.useEffect(() => {
		if (templateQuery.data?.content) {
			setPostcardContent(Api.getContentHtmlStringValueFromRawRichTextContentState(templateQuery.data.content));
		}
	}, [templateQuery.data?.content]);
	const { handwritingStyle, postcardText, getPostcardTextEditorProps } = usePostcardTextEditor({
		content: postcardContent,
		defaultHandwritingStyle: userSession.user.userPreferences?.handwrittenCardPreferences?.handwritingStyle,
		onContentChanged: setPostcardContent,
	});

	const [signature, setSignature] = React.useState(
		sendFrom?.sender?.mode === Api.SendEmailFrom.CurrentUser
			? (userSession.user!.userPreferences?.handwrittenCardPreferences?.signature ?? '')
			: ''
	);
	const sendFromUserHwcPreferencesQuery = useUserHwcPreferences({
		enabled: sendFromUser?.id != null,
		forUserId: sendFromUser?.id,
	});
	const sendFromUserQuery = useUserQuery({
		enabled: sendFromUser?.id != null,
		id: sendFromUser?.id,
	});
	React.useEffect(() => {
		if (sendFromUserHwcPreferencesQuery?.data?.signature) {
			setSignature(sendFromUserHwcPreferencesQuery.data.signature);
		}
	}, [sendFromUserHwcPreferencesQuery?.data?.signature]);
	React.useEffect(() => {
		if (sendFromUserQuery.data) {
			setReturnContact(
				sendFromUserQuery.data.userPreferences?.handwrittenCardPreferences?.returnContact ??
					userSession.account?.preferences?.handwrittenCardPreferences?.returnContact
			);
		}
	}, [sendFromUserQuery.data, userSession]);
	const [datePickerOpen, setDatePickerOpen] = React.useState(false);

	const [mailDate, setMailDate] = React.useState<Date>(null);
	const { handleRecipientChange, hwcRecipient, contactQuery } = useRecipient({ contactId });
	const recipientAddressText =
		hwcRecipient && hasRequiredHwcRecipientAddressFields(hwcRecipient.address)
			? addressTextFromHwcRecipient(hwcRecipient)
			: '';
	const [returnContact, setReturnContact] = React.useState<Api.IHandwrittenCardReturnContact>(
		userSession.user?.userPreferences?.handwrittenCardPreferences?.returnContact ??
			userSession.account?.preferences?.handwrittenCardPreferences?.returnContact
	);
	const returnAddressText =
		returnContact && isAddressComplete(returnContact.address) ? addressTextFromHwcReturnContact(returnContact) : '';
	const mailDateText = mailDate ? moment(mailDate).format('YYYY-MM-DD') : '';
	const [showRecipientAddress, setShowRecipientAddress] = React.useState(false);
	const [showReturnContactModal, setShowReturnContactModal] = React.useState(false);
	const [toggledPreview, setToggledPreview] = React.useState<HwcTogglePreviewValues>('text');
	const [showingDeleteTemplateConfirmation, setShowingDeleteTemplateConfirmation] = React.useState(false);
	const [showPurchaseCreditsModal, setShowPurchaseCreditsModal] = React.useState(false);
	const { balanceAfterSend, currentBalance, giftingQuery } = useCurrentUserGiftingBalance();
	const canPlaceOrder =
		postcardText &&
		mailDate &&
		(returnContact ||
			![Api.SendEmailFrom.CurrentUser, Api.SendEmailFrom.SelectedUser].includes(sendFrom?.sender?.mode)) &&
		hwcRecipient &&
		currentBalance > 0 &&
		balanceAfterSend >= 0;
	const handleShowRecipientAddress = () => setShowRecipientAddress(true);
	const handleShowReturnContactModal = () => setShowReturnContactModal(true);
	const orderMutation = useHWCOrderMutation({
		onError: error => {
			toast.error(error.systemMessage);
			logApiError('orderMutation', error);
		},
		onSuccess: () => {
			fullscreenModal.history.replace({
				pathname: `/postcard/${templateId}/${PostcardComposerStep.Success}`,
				state: location.state,
			});
			toast.success('Your handwritten card has been scheduled!');
		},
	});
	const templateDeleteMutation = useTemplateDeleteMutation({
		onError: error => {
			logApiError('templateDeleteMutation', error);
			errorMessages.pushApiError(error);
		},
		onSuccess: () => {
			setShowingDeleteTemplateConfirmation(false);
			fullscreenModal.dismissModal();
			toast.success('Card deleted successfully');
			invalidateMyTemplates({ templateType: Api.TemplateType.HandwrittenCard });
			invalidateHandwrittenCardAllCategoryTemplates();
		},
	});
	const renderImagePreview = () => {
		return (
			<div className={css(styleSheet.mainPreview, styleSheet.mainPreviewModeImage)}>
				{templateQuery.data ? (
					<img
						src={templateQuery.data.image.publicUrl}
						alt='Template image preview'
						className={css(styleSheet.mainPreviewImage)}
					/>
				) : (
					<LoadingSpinner />
				)}
			</div>
		);
	};

	return (
		<div className={css(styleSheet.root)}>
			{step === PostcardComposerStep.Success ? (
				<>
					<MultiContainerHeader fullscreenHeader='Order Confirmation' />
					<HwcOrderSuccessPage />
				</>
			) : null}
			{step !== PostcardComposerStep.Success ? (
				<>
					<MultiContainerHeader fullscreenHeader='View Card' />
					<section className={css(styleSheet.mainPreviewContainer)}>
						<div className={css(styleSheet.actionsToolbar)}>
							<div>
								<SendFromDropdown
									disabled={!userSession.canSendOnBehalf}
									canSendOnBehalf={userSession.canSendOnBehalf}
									allowedContactBasedOptions='allWithoutCustomSendOnBehalfPermissions'
									sendFromOption={sendFrom}
									sendFromUserId={sendFromUser?.id}
									sendOnBehalfOf={(option, user) => {
										setSendFrom(option);
										setSendFromUser(user);
										switch (option?.sender?.mode) {
											case Api.SendEmailFrom.CurrentUser:
												// Set signature to user's signature when current user is selected
												setSignature(userSession.user.userPreferences?.handwrittenCardPreferences?.signature ?? '');
												setReturnContact(
													userSession.user.userPreferences?.handwrittenCardPreferences?.returnContact ??
														userSession.account?.preferences?.handwrittenCardPreferences?.returnContact
												);
												break;
											case Api.SendEmailFrom.ContactOwner:
												// Clear signature when contact owner is selected
												setSignature('');
												break;
											default:
												break;
										}
									}}
								/>
							</div>
							<div className={css(bs.flex, bs.itemsCenter, bs.gap2)}>
								{[
									PostcardComposerStep.Personalize,
									PostcardComposerStep.Recipient,
									PostcardComposerStep.Order,
								].includes(step) ? (
									<ButtonGroup>
										{isSaveAllowed ? (
											<>
												<Button
													size='sm'
													variant='secondary'
													onClick={handleSaveButtonClicked}
													disabled={updatingTemplateStatus === 'pending' || !postcardContent}
												>
													Save
												</Button>
												<Dropdown>
													<DropdownTrigger render={<Button size='sm' variant='secondary' />}>
														<ChevronDown />
													</DropdownTrigger>
													<DropdownContent align='end'>
														<DropdownItem onClick={handleSaveAsButtonClicked}>Save as</DropdownItem>
													</DropdownContent>
												</Dropdown>
											</>
										) : null}
										{!isSaveAllowed ? (
											<Button variant='secondary' size='sm' onClick={handleSaveAsButtonClicked}>
												Save as
											</Button>
										) : null}
									</ButtonGroup>
								) : null}
								{(templateQuery.data?.scope === Api.TemplateScope.Account ||
									templateQuery.data?.scope === Api.TemplateScope.User) &&
								(templateQuery.data?.creator?.id === userSession.user.id ||
									new RegExp(/admin/i).test(userSession.user.role)) ? (
									<Button
										variant='destructive-secondary'
										size='sm'
										onClick={() => setShowingDeleteTemplateConfirmation(true)}
									>
										Delete
									</Button>
								) : null}
							</div>
						</div>
						<div className={css(styleSheet.mainPreviewContainer2)}>
							<div className={css(styleSheet.previewWrapper)}>
								<p className={css(styleSheet.cardPreviewText)}>Card Preview</p>
								{step === PostcardComposerStep.Preview ? (
									renderImagePreview()
								) : (
									<HwcFrontBackPreview
										templateId={templateId}
										value={toggledPreview}
										handwritingStyle={handwritingStyle}
										signature={disableSignatureEditing ? cardSignaturePlaceholderText : signature}
										postcardText={postcardText}
										onValueChange={setToggledPreview}
									/>
								)}
							</div>
						</div>
					</section>
					<section className={css(styleSheet.configDrawer)}>
						{step === PostcardComposerStep.Preview ? (
							<>
								{templateQuery.data ? (
									<h2 className={css(styleSheet.configDrawerHeader)}>{templateQuery.data.name}</h2>
								) : (
									<LoadingSpinner type='small' />
								)}
								<div className={css(styleSheet.divider)} />
								<div className={css(styleSheet.cardInfoList)}>
									<div className={css(styleSheet.cardInfoItem)}>
										<PostcardSpec width={40} />
										<div>
											<div className={css(styleSheet.cardInfoHeader)}>Specs</div>
											<div className={css(styleSheet.cardInfoText)}>5 x 7”</div>
										</div>
									</div>
									<div className={css(styleSheet.cardInfoItem)}>
										<PostcardType width={40} />
										<div>
											<div className={css(styleSheet.cardInfoHeader)}>Card Type</div>
											<div className={css(styleSheet.cardInfoText)}>Folded</div>
										</div>
									</div>
									<div className={css(styleSheet.cardInfoItem)}>
										<PostcardPostage width={40} />
										<div>
											<div className={css(styleSheet.cardInfoHeader)}>Postage</div>
											<div className={css(styleSheet.cardInfoText)}>Postage Included</div>
										</div>
									</div>
								</div>
								<div className={css(styleSheet.divider)} />
								<button
									className={css(baseStyleSheet.ctaButton, styleSheet.personalizeButton)}
									onClick={() =>
										fullscreenModal.history.push({
											pathname: `/postcard/${templateId}/${PostcardComposerStep.Personalize}`,
											state: location.state,
										})
									}
								>
									Personalize & Send
								</button>
							</>
						) : null}
						{step === PostcardComposerStep.Personalize ? (
							<>
								<h2 className={css(styleSheet.configDrawerHeader)}>Personalization</h2>
								<div className={css(styleSheet.configDrawerSection)}>
									<h3 className={css(styleSheet.configDrawerMessage)}>Message</h3>
									<span
										className={css(
											styleSheet.configDrawerSubtitle,
											styleSheet.configDrawerCharCount,

											postcardText.length > HANDWRITTEN_CARD_TEXT_MAX_ALLOWED_CHARACTERS &&
												styleSheet.configDrawerCharCountOver
										)}
									>
										{postcardText.length}/{HANDWRITTEN_CARD_TEXT_MAX_ALLOWED_CHARACTERS}
									</span>
									<PostcardTextEditor {...getPostcardTextEditorProps()} />
								</div>
								<div className={css(styleSheet.configDrawerSection)}>
									<h4 className={css(styleSheet.configDrawerSelectSig)}>Signature</h4>
									<TextArea
										value={disableSignatureEditing ? cardSignaturePlaceholderText : signature}
										disabled={disableSignatureEditing}
										onChange={ev => setSignature(ev.target.value)}
									/>
								</div>
								<button
									className={css(baseStyleSheet.ctaButton)}
									onClick={() => {
										if (location.state?.contactId == null) {
											fullscreenModal.history.push({
												pathname: `/postcard/${templateId}/${PostcardComposerStep.Recipient}`,
												state: location.state,
											});
											return;
										}

										fullscreenModal.history.push({
											pathname: `/postcard/${templateId}/${PostcardComposerStep.Order}`,
											state: location.state,
										});
									}}
									disabled={!postcardText || postcardText.length > HANDWRITTEN_CARD_TEXT_MAX_ALLOWED_CHARACTERS}
								>
									{location.state?.contactId == null ? 'Next: Choose Recipient' : `Next: Review Order`}
								</button>
							</>
						) : null}
						{step === PostcardComposerStep.Recipient ? (
							<>
								<h2 className={css(styleSheet.configDrawerHeader, styleSheet.configDrawerHeaderRecipient)}>
									Select Recipient
								</h2>
								<p
									className={css(
										styleSheet.configDrawerSubtitle,
										styleSheet.configDrawerSubtitleRecipient,
										styleSheet.mb40
									)}
								>
									Search your contacts for who you would to recieve this card
								</p>
								<div className={css(styleSheet.divider, styleSheet.mb40)} />
								<p className={css(styleSheet.selectRecipientToSend, styleSheet.mb16)}>Select recipient to send:</p>
								{selectedContact ? (
									<>
										{contactQuery.isPending ? (
											<div className={css(styleSheet.selectRecipientContactSpinnerContainer)}>
												<LoadingSpinner type='small' />
											</div>
										) : null}
										{contactQuery.data ? (
											<div className={css(styleSheet.selectRecipientContactPreview)}>
												<DeprecatedCloseButton onClick={() => setSelectedContact(null)} />
												<Avatar2
													imgSrc={contactQuery.data.profilePic}
													altText={contactQuery.data ? getDisplayName(contactQuery.data) : ''}
													fallbackText={getPrincipalInitials(contactQuery.data)}
													styles={[styleSheet.selectRecipientContactPreviewAvatar]}
												/>
												<div className={css(styleSheet.selectRecipientContactNameContainer)}>
													<div className={css(baseStyleSheet.truncateText, styleSheet.selectRecipientContactName)}>
														{getDisplayName(contactQuery.data)}
													</div>
													{contactQuery.data.companyName ? (
														<div className={css(baseStyleSheet.truncateText, styleSheet.selectRecipientContactCompany)}>
															{getContactCompanyLine(contactQuery.data)}
														</div>
													) : null}
												</div>
											</div>
										) : null}
									</>
								) : (
									<GlobalSearch
										onContactSelected={contact => setSelectedContact(contact)}
										resultsStyles={[styleSheet.selectRecipientContactResults]}
									/>
								)}
								<div className={css(styleSheet.divider, styleSheet.mt40, styleSheet.mb56)} />
								<button
									className={css(baseStyleSheet.ctaButton)}
									onClick={() => {
										fullscreenModal.history.push({
											pathname: `/postcard/${templateId}/${PostcardComposerStep.Order}`,
											state: location.state,
										});
									}}
									disabled={!selectedContact}
								>
									Next: Review Order
								</button>
							</>
						) : null}
						{step === PostcardComposerStep.Order ? (
							<>
								<h2 className={css(styleSheet.configDrawerHeader)}>Review Order</h2>
								<div className={css(styleSheet.divider, styleSheet.dividerMargin)} />
								<div className={css(styleSheet.configDrawerSection)}>
									<strong className={css(styleSheet.configDrawerThirdHeader)}>
										Mail Date <span className={css(styleSheet.requiredIndicator)}>*</span>
									</strong>
									<span className={css(styleSheet.configDrawerSubtitle, styleSheet.mb16)}>
										Please allow up to 10 days for delivery
									</span>
									<DeprecatedPopover
										anchor={
											<span>
												<TextInput
													inputId='postcard-mail-date'
													type='date'
													onChange={ev => {
														const parsed = moment(ev.target.value);
														if (parsed.isValid()) {
															setMailDate(parsed.toDate());
														}
													}}
													onClick={() => setDatePickerOpen(true)}
													value={mailDateText}
												/>
											</span>
										}
										dismissOnClickOutside={true}
										isOpen={datePickerOpen}
										onRequestClose={() => setDatePickerOpen(false)}
										type={PopoverType.white}
										preferredPlacement='below'
									>
										<DayPicker month={mailDate} onDayClick={setMailDate} selectedDays={mailDate} />
									</DeprecatedPopover>
								</div>
								{sendFrom?.sender?.mode === Api.SendEmailFrom.CurrentUser ||
								sendFrom?.sender?.mode === Api.SendEmailFrom.SelectedUser ? (
									<AddressFormField
										address={returnAddressText}
										btnLabel='Add Return Address'
										id='return-address'
										label='Return Address'
										onBtnClick={handleShowReturnContactModal}
										onEditBtnClick={handleShowReturnContactModal}
										required={true}
									/>
								) : null}
								{contactQuery.isPending ? <LoadingSpinner /> : null}
								{contactQuery.status === 'error' ? (
									<p role='error' className={css(styleSheet.errorContactLoad)}>
										Failed to load contact&apos;s address
									</p>
								) : null}
								{contactQuery.status === 'success' ? (
									<AddressFormField
										address={recipientAddressText}
										btnLabel='Add Recipient Address'
										id='recipient-address'
										label='Recipient Address'
										onBtnClick={handleShowRecipientAddress}
										onEditBtnClick={handleShowRecipientAddress}
										required={true}
									/>
								) : null}
								<div className={css(styleSheet.divider, styleSheet.dividerMargin)} />
								{giftingQuery.isPending ? <LoadingSpinner /> : null}
								{giftingQuery.status === 'error' ? (
									<p role='error' className={css(styleSheet.errorContactLoad)}>
										Failed to load balance data
									</p>
								) : null}
								{giftingQuery.status === 'success' ? (
									<>
										<p className={css(styleSheet.balanceContainer)}>
											<strong className={css(styleSheet.balanceText)}>Current Balance:</strong>
											{new Intl.NumberFormat('en-US', { currency: 'USD', style: 'currency' }).format(currentBalance)}
										</p>

										{balanceAfterSend >= 0 || !Api.isAdmin(userSession.user) ? (
											<p className={css(styleSheet.balanceContainer)}>
												<strong className={css(styleSheet.balanceText)}>Balance After Send: </strong>{' '}
												{new Intl.NumberFormat('en-US', { currency: 'USD', style: 'currency' }).format(
													balanceAfterSend
												)}
											</p>
										) : (
											<button
												className={css(baseStyleSheet.ctaButtonSecondary)}
												onClick={() => setShowPurchaseCreditsModal(true)}
											>
												+ Add credits to continue
											</button>
										)}
									</>
								) : null}
								<div className={css(styleSheet.divider, styleSheet.dividerMargin)} />
								{userSession.account?.features?.handwrittenCards?.creditCostPerCard != null ? (
									<p className={css(styleSheet.balanceContainer)}>
										<strong className={css(styleSheet.total)}>Total:</strong>
										{new Intl.NumberFormat('en-US', { currency: 'USD', style: 'currency' }).format(
											userSession.account.features.handwrittenCards.currencyCostPerCard
										)}
									</p>
								) : null}
								<div className={css(styleSheet.divider, styleSheet.dividerMargin)} />
								<button
									className={css(baseStyleSheet.ctaButton)}
									disabled={orderMutation.isPending || !canPlaceOrder}
									onClick={() => {
										const content = createContentStateWithHtmlStringValue(postcardContent).getRawRichTextContent();
										orderMutation.mutate({
											content,
											recipient: hwcRecipient,
											returnContact: [Api.SendEmailFrom.CurrentUser, Api.SendEmailFrom.SelectedUser].includes(
												sendFrom?.sender?.mode
											)
												? returnContact
												: undefined,
											scheduledSendDate: mailDate.toISOString(),
											signature,
											style: handwritingStyle,
											templateId,
											sendFrom: sendFrom?.sender?.mode,
											senderUserId: sendFromUser?.id,
											sendFromConnectionType: sendFrom?.sender?.connectionType,
										});
									}}
								>
									Place Order
								</button>
							</>
						) : null}
					</section>
				</>
			) : null}
			<FabContext appearance={{ hidden: true }} />
			{showRecipientAddress ? (
				<RecipientAddressModal
					isOpen={showRecipientAddress}
					initialData={{
						addresseeName: addresseeNameFromHwcRecipient(hwcRecipient),
						address1: hwcRecipient.address?.address1 ?? '',
						address2: hwcRecipient.address?.address2 ?? '',
						city: hwcRecipient.address?.city ?? '',
						salutationName: hwcRecipient.firstName ?? '',
						postalCode: hwcRecipient.address?.postalCode ?? '',
						stateProvince: hwcRecipient.address?.stateProvince ?? '',
					}}
					onSave={data => {
						handleRecipientChange({
							...data,
							address: {
								name: data.addresseeName,
								address1: data.address1,
								address2: data.address2,
								city: data.city,
								postalCode: data.postalCode,
								stateProvince: data.stateProvince,
							},

							contactId,
							firstName: data.salutationName,
						});
						setShowRecipientAddress(false);
					}}
					onCancel={() => setShowRecipientAddress(false)}
				/>
			) : null}
			{showReturnContactModal ? (
				<ReturnContactModal
					isOpen={showReturnContactModal}
					initialRecipient={returnContact}
					onSave={data => {
						setReturnContact({
							...data,
							address: {
								address1: data.address1,
								address2: data.address2,
								city: data.city,
								country: data.country,
								postalCode: data.postalCode,
								stateProvince: data.stateProvince,
							},
							firstName: data.firstName,
							lastName: data.lastName,
						});
						setShowReturnContactModal(false);
					}}
					onCancel={() => setShowReturnContactModal(false)}
				/>
			) : null}
			{isShowingSaveAsModal ? (
				<Modal
					useDefaultHeader
					className={css(styleSheet.templateSaveModal)}
					isOpen={true}
					shouldCloseOnOverlayClick={false}
					onRequestClose={handleSaveAsModalClose}
				>
					<SaveTemplateModal
						initialTemplateScope={
							templateQuery.data?.scope === Api.TemplateScope.Account
								? Api.TemplateScope.Account
								: Api.TemplateScope.User
						}
						actionButtonsDisabled={createTemplateMutation.isPending}
						onSaveButtonClicked={({ templateName, scope }) => {
							handleSaveAsModalSave({ name: templateName, scope });
						}}
						onCancelButtonClicked={handleSaveAsModalClose}
					/>
				</Modal>
			) : null}
			<DeleteConfirmation
				bodyText={`Are you sure you want to delete your card named ${templateQuery.data?.name}?`}
				destructiveButtonText='Yes, Delete'
				isOpen={showingDeleteTemplateConfirmation}
				onFinish={shouldDelete => {
					if (shouldDelete) {
						templateDeleteMutation.mutate({ id: templateQuery.data.id });
						return;
					}
					setShowingDeleteTemplateConfirmation(false);
				}}
				showSpinner={templateDeleteMutation.isPending}
			/>
			<PurchaseCreditsModal
				isOpen={showPurchaseCreditsModal}
				onRequestClose={() => setShowPurchaseCreditsModal(false)}
			/>
		</div>
	);
}
